  import { useEffect, useState, useRef } from "react";
  import { useParams, useNavigate } from "react-router-dom";
  import { db } from "../../firebase";
  import { doc, getDoc, updateDoc, onSnapshot, collection, getDocs, query, where } from "firebase/firestore";
  import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
  import { acceptJob, startWork, endJob } from "../../pages/services/serviceTaskService";
  import { markNotificationReadByReference } from "../../services/notificationService";
  import { createSparePartRequest } from "../../services/sparePartService";
  import DefectiveItemForm from "../../components/DefectiveItemForm";
  import { useLang } from "../../i18n/useLang";
  import LangToggle from "../../i18n/LangToggle";

  // Status → label key in technicianStrings.js. Without this the page showed
// the raw value, and "completed_pending_approval" rendered blank because it
// had no colour either (D-308).
const STATUS_LABEL_KEYS = {
  open: "status_open",
  assigned: "status_assigned",
  in_progress: "status_in_progress",
  partial: "status_partial",
  completed_pending_approval: "status_pending_approval",
  completed: "status_completed",
  closed: "status_closed",
  cancelled: "status_cancelled",
  rework: "status_rework",
};

const getDistanceMeters = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  export default function TaskDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const user    = JSON.parse(localStorage.getItem("user") || "{}");
    const techId  = user.uid || user.id || "";
    const techName = user.name || "";

    // i18n — Hinglish/English toggle for this technician-facing page.
    // Only technician pages use this; manager views (ServiceDetail.js) never
    // pass `lang` to DefectiveItemForm, so they stay English by default.
    // useLang(user) needs the user object (for the Firestore preferred_lang
    // sync + uid) — `user` is already parsed from localStorage above.
    const { lang, setLang, t } = useLang(user);

    const [task, setTask] = useState(null);
    const [locating, setLocating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showReport, setShowReport] = useState(false);

    const [reportData, setReportData] = useState({
      issue_found: "",
      resolution: "",
      service_report: "",
      completion_status: "completed"
    });
    const storage = getStorage();
    const MAX_PHOTOS_PER_JOB = 5;
    const [uploadingPhotos, setUploadingPhotos] = useState(false);

    // Real upload handler — job can have photos uploaded any time while
    // in_progress, independent of the Complete Job report. Mirrors
    // ServiceDetail.js's (manager-side) governed pattern: unique filename
    // per upload (timestamp + original name) prevents mobile-camera
    // photos reusing the same filename from silently overwriting each
    // other in Storage, and a 5-photo-per-job cap is enforced.
    const handlePhotoUpload = async (e) => {
      const files = e.target.files;
      if (!files.length) return;

      const existingCount = (task?.photos || []).length;
      const remainingSlots = MAX_PHOTOS_PER_JOB - existingCount;

      if (remainingSlots <= 0) {
        alert(`❌ Maximum ${MAX_PHOTOS_PER_JOB} photos already uploaded for this job.`);
        e.target.value = "";
        return;
      }

      const filesToUpload = Array.from(files).slice(0, remainingSlots);
      const skippedCount = files.length - filesToUpload.length;

      setUploadingPhotos(true);
      try {
        const urls = [];
        for (let file of filesToUpload) {
          const uniqueName = `${Date.now()}_${file.name}`;
          const r = ref(storage, `services/${id}/${uniqueName}`);
          await uploadBytes(r, file);
          urls.push(await getDownloadURL(r));
        }
        await updateDoc(doc(db, "service_tasks", id), {
          photos: [...(task?.photos || []), ...urls],
        });

        if (skippedCount > 0) {
          alert(`✅ ${filesToUpload.length} photo(s) uploaded. ⚠️ ${skippedCount} skipped — max ${MAX_PHOTOS_PER_JOB} photos per job.`);
        } else {
          alert("✅ Photos Uploaded!");
        }
      } catch (err) {
        alert("Error: " + err.message);
      } finally {
        setUploadingPhotos(false);
        e.target.value = "";
      }
    };
    const [payment, setPayment] = useState({
      received: null,
      amount: "",
      mode: "cash",
    });

    const [remarks, setRemarks] = useState("");
    const [showDefectiveForm, setShowDefectiveForm] = useState(false);
    const [addingDefective, setAddingDefective] = useState(false); // prevents double-click → duplicate defective_items entries
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason,    setRejectReason]    = useState("");
    const [defectiveItem, setDefectiveItem] = useState({
      item_name: "", qty: 1,
      machine_id: "", machine_description: "", machine_model: "",
      serial_no: "", part_name: "", part_code: "", remarks: "",
    });

    // ── Spare Parts ──────────────────────────────────────────────────────
    const [siteMachines, setSiteMachines] = useState([]);
    const [sparePartRequests, setSparePartRequests] = useState([]);
    const [showSparePartForm, setShowSparePartForm] = useState(false);
    const [submittingSparePart, setSubmittingSparePart] = useState(false);
    const [sparePartItem, setSparePartItem] = useState({
    part_name: "", part_no: "", serial_no: "", brand: "",
    quantity: 1, machine_id: "", notes: "",
    // is_serialized removed — this system tracks ONLY serialized parts,
    // so every request is serialized by definition. No longer a user
    // decision/checkbox.
  });

    // ── Auto-scroll refs ─────────────────────────────────────────────────
    // Every "open a form below" button (Defective Item, Spare Part,
    // Complete Job) used to open its form silently off-screen — the
    // technician had no visual cue and had to scroll down manually to
    // even notice it opened. These refs + effects scroll the newly-opened
    // form into view automatically the moment its toggle flips true.
    const defectiveFormRef = useRef(null);
    const sparePartFormRef = useRef(null);
    const reportFormRef = useRef(null);

    useEffect(() => {
      if (showDefectiveForm && defectiveFormRef.current) {
        defectiveFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, [showDefectiveForm]);

    useEffect(() => {
      if (showSparePartForm && sparePartFormRef.current) {
        sparePartFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, [showSparePartForm]);

    useEffect(() => {
      if (showReport && reportFormRef.current) {
        reportFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, [showReport]);

    // Real-time listener — remarks (and any other field) update live without
    // needing a manual refresh, so chat-style remarks feel instant.
    useEffect(() => {
      const ref = doc(db, "service_tasks", id);
      const unsubscribe = onSnapshot(ref, (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data.company_id !== user.company_id) {
            alert("Access denied — this job belongs to a different company");
            navigate(-1);
            setLoading(false);
            return;
          }
          setTask(data);
        }
        setLoading(false);
      }, (err) => {
        console.error("TaskDetail onSnapshot error:", err);
        setLoading(false);
      });
      return () => unsubscribe();
    }, [id]);

    // Spare Parts — fetch site machines (for the machine picker) + existing
    // requests raised for this service job. Read-only queries — consistent
    // with the existing direct-read pattern already used elsewhere in this file.
    useEffect(() => {
      fetchSparePartData();
    }, [id, task?.site_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchSparePartData = async () => {
      if (!user.company_id) return;
      try {
        if (task?.site_id) {
          const mSnap = await getDocs(query(
            collection(db, "site_machines"),
            where("company_id", "==", user.company_id),
            where("site_id", "==", task.site_id)
          ));
          setSiteMachines(mSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        }
        const rSnap = await getDocs(query(
          collection(db, "spare_part_requests"),
          where("company_id", "==", user.company_id),
          where("service_id", "==", id)
        ));
        const requests = rSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        requests.sort((a, b) => (b.requested_at?.seconds || 0) - (a.requested_at?.seconds || 0));
        setSparePartRequests(requests);
      } catch (err) {
        console.warn("Spare part data fetch failed (non-blocking):", err.message);
      }
    };

    // Opening this job resolves any pending "new remark" notification for it —
    // viewing the chat thread counts as having seen the message.
    useEffect(() => {
      markNotificationReadByReference(id, "new_remark", user.company_id);
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Kept for places that call loadTask() after an action (no-op now —
    // onSnapshot already keeps task fresh — retained so existing calls don't break).
    const loadTask = async () => {};

    const handleAccept = async () => {
      try {
        await acceptJob({ task_id: id, tech_id: techId, tech_name: techName });
        alert("✅ Job Accepted!");
        loadTask();
      } catch (err) {
        alert("Error: " + err.message);
      }
    };

    const handleStart = async () => {
      setLocating(true);
      try {
        // Step 1 — site ka lat/lng fetch karo
        let siteLat = null;
        let siteLng = null;
        if (task?.site_id) {
          try {
            const siteSnap = await getDoc(doc(db, "sites", task.site_id));
            if (siteSnap.exists()) {
              siteLat = siteSnap.data().lat || null;
              siteLng = siteSnap.data().lng || null;
            }
          } catch (e) {
            console.warn("Site fetch failed:", e.message);
          }
        }

        // Step 2 — GPS lo
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            const techLat = pos.coords.latitude;
            const techLng = pos.coords.longitude;

            // Step 3 — geofence check (sirf agar site mein lat/lng set hai)
            if (siteLat && siteLng) {
              const distance = getDistanceMeters(techLat, techLng, siteLat, siteLng);
              if (distance > 300) {
                setLocating(false);
                alert(
                  `⚠️ You are ${Math.round(distance)} meters away from the job site.\n\nPlease reach the site (within 300m) to start the job.`
                );
                return;
              }
            }

            // Step 4 — within range ya no geofence — start karo
            setLocating(false);
            try {
              await startWork({
                task_id:           id,
                tech_id:           techId,
                tech_name:         techName,
                company_id:        user.company_id || "",
                start_location:    { lat: techLat, lng: techLng, accuracy: pos.coords.accuracy },
                start_location_at: new Date().toISOString(),
                start_attempt_at:  new Date().toISOString(),
              });
              await markNotificationReadByReference(id, "job_assigned", user.company_id);
              alert("✅ Job Started!");
              loadTask();
            } catch (err) {
              alert("Error: " + err.message);
            }
          },
          async (err) => {
            // GPS unavailable — geofence skip, directly start
            setLocating(false);
            try {
              await startWork({
                task_id:          id,
                tech_id:          techId,
                tech_name:        techName,
                company_id:       user.company_id || "",
                start_location:   null,
                location_skipped: "gps_unavailable",
                start_attempt_at: new Date().toISOString(),
              });
              await markNotificationReadByReference(id, "job_assigned", user.company_id);
              alert("✅ Job Started! (Location unavailable)");
              loadTask();
            } catch (e) {
              alert("Error: " + e.message);
            }
          },
          { timeout: 10000, enableHighAccuracy: true }
        );
      } catch (err) {
        setLocating(false);
        alert("Error: " + err.message);
      }
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      setReportData((prev) => ({ ...prev, [name]: value }));
    };

  const handleComplete = async () => {
      if (submitting) return;
      if (!reportData.service_report?.trim()) {   // D-308: only the detailed report is required
        alert(t("detailed_report_required")); return;
      }
      if (payment.received === null) {
        alert("❌ Payment received — Yes ya No select karo"); return;
      }
      if (payment.received && !payment.amount) {
        alert("❌ Amount enter karo"); return;
      }
      setSubmitting(true);
      try {
        await endJob({
          task_id:               id,
          tech_id:               techId,
          tech_name:             techName,
          company_id:            user.company_id || "",
          completion_type:       reportData.completion_status === "partial" ? "partial" : "complete",
          service_report:        reportData.service_report,
          issue_found:           reportData.issue_found,
          resolution:            reportData.resolution,
          site_payment_received: Boolean(payment.received),
          site_payment_amount:   payment.received ? Number(payment.amount) : 0,
          site_payment_mode:     payment.received ? payment.mode : "",
        });
        alert("✅ Job Submitted for Approval!");
        setShowReport(false);
        loadTask();
      } catch (err) {
        alert("Error: " + err.message);
      } finally {
        setSubmitting(false);
      }
    };

    const handleReject = async () => {
      if (!rejectReason.trim()) { alert("Reason required ❌"); return; }
      try {
        const { rejectJob } = await import("../../pages/services/serviceTaskService");
        await rejectJob({ task_id: id, tech_id: techId, tech_name: techName, reason: rejectReason.trim() });
        alert("✅ Job Rejected!");
        setShowRejectModal(false);
        setRejectReason("");
        loadTask();
      } catch (err) { alert("Error: " + err.message); }
    };

    // onSnapshot listener already refreshes task in real time — no need to
    // alert() or manually reload after saving a remark.
    const handleRemark = async () => {
      if (!remarks.trim()) return;
      try {
        const { addRemark } = await import("../../pages/services/serviceTaskService");
        await addRemark({ task_id: id, remark: remarks, added_by: techId, added_by_name: techName });
        setRemarks("");
      } catch (err) { alert("Error: " + err.message); }
    };

    const addDefective = async () => {
      if (addingDefective) return;
      if (!defectiveItem.item_name.trim()) { alert("Item name required ❌"); return; }
      setAddingDefective(true);
      try {
        const { addDefectiveItem } = await import("../../pages/services/serviceTaskService");
        await addDefectiveItem({
          task_id:             id,
          item_name:           defectiveItem.item_name.trim(),
          qty:                 Number(defectiveItem.qty || 1),
          machine_id:          defectiveItem.machine_id || "",
          machine_description: defectiveItem.machine_description.trim(),
          machine_model:       defectiveItem.machine_model.trim(),
          serial_no:           defectiveItem.serial_no.trim(),
          part_name:           defectiveItem.part_name.trim(),
          part_code:           defectiveItem.part_code.trim(),
          remarks:             defectiveItem.remarks.trim(),
        });
        setDefectiveItem({ item_name: "", qty: 1, machine_id: "", machine_description: "", machine_model: "", serial_no: "", part_name: "", part_code: "", remarks: "" });
        setShowDefectiveForm(false);
        alert("✅ Defective item added!");
        loadTask();
      } catch (err) { alert("Error: " + err.message); }
      finally { setAddingDefective(false); }
    };

    // Shared navigation target for "add a machine at this site" — used by
    // BOTH the standalone "🖥 Add Machine at this Site" button below AND
    // DefectiveItemForm's empty-state "+ Machine Add Karo" button, so there
    // is exactly one place that builds this URL.
    const goToAddMachine = () => {
      const params = new URLSearchParams({
        client_id:   task?.client_id   || "",
        client_name: task?.client_name || "",
        site_id:     task?.site_id     || "",
        site_name:   task?.site_name   || "",
        task_id:     id,
      });
      navigate(`/machines/add?${params.toString()}`);
    };

    // Prefill the spare-part form from an existing defective item — avoids
    // retyping part_name/part_no/serial_no the technician already recorded.
   const prefillSparePartFromDefective = (item, idx) => {
    setSparePartItem({
      part_name:   item.item_name || "",
      part_no:     item.part_code || "",
      serial_no:   item.serial_no || "",
      brand:       item.part_name || "",
      quantity:    item.qty || 1,
      machine_id:  item.machine_id || "",
      notes:       `From defective item: ${item.item_name || ""}`,
      linked_defective_item_id: String(idx),
    });
    setShowSparePartForm(true);
  };

    const submitSparePart = async () => {
    if (submittingSparePart) return;
    if (!sparePartItem.part_name.trim()) { alert("❌ Part name required"); return; }
    if (!sparePartItem.serial_no.trim()) { alert("❌ Serial No. required — this system tracks serialized parts only"); return; }
    if (!sparePartItem.quantity || Number(sparePartItem.quantity) < 1) { alert("❌ Quantity must be at least 1"); return; }

      const duplicate = sparePartRequests.find(r =>
        r.status !== "closed" &&
        (r.part_name || "").trim().toLowerCase() === sparePartItem.part_name.trim().toLowerCase()
      );
      if (duplicate) {
        const proceed = window.confirm(
          `⚠️ A similar open request already exists for "${sparePartItem.part_name}" (${duplicate.request_no}, status: ${duplicate.status.replace(/_/g, " ")}).\n\nSubmit this as a separate request anyway?`
        );
        if (!proceed) return;
      }

      setSubmittingSparePart(true);
      try {
       await createSparePartRequest({
        company_id:   user.company_id || "",
        service_id:   id,
        project_id:   task?.project_id || "",
        site_id:      task?.site_id || "",
        client_id:    task?.client_id || "",
        machine_id:   sparePartItem.machine_id || "",
        part_name:    sparePartItem.part_name.trim(),
        part_no:      sparePartItem.part_no.trim(),
        serial_no:    sparePartItem.serial_no.trim(),
        brand:        sparePartItem.brand.trim(),
        quantity:     Number(sparePartItem.quantity),
        linked_defective_item_id: sparePartItem.linked_defective_item_id || "",
        notes:        sparePartItem.notes.trim(),
        requested_by: techId,
        requested_by_name: techName,
      });
      setSparePartItem({ part_name: "", part_no: "", serial_no: "", brand: "", quantity: 1, machine_id: "", notes: "" });
        setShowSparePartForm(false);
        alert("✅ Spare part requested! Store/Manager will review it.");
        fetchSparePartData();
      } catch (err) {
        alert("Error: " + err.message);
      } finally {
        setSubmittingSparePart(false);
      }
    };

    if (loading) return <p style={{ padding: 20 }}>Loading...</p>;

    // Reject Modal — rendered inside return

    const formatMinutes = (mins) => {
      if (mins < 0) return "-";
      if (mins < 60) return `${mins}m`;
      return `${Math.floor(mins / 60)}h ${mins % 60}m`;
    };

    const getDuration = () => {
    if (task?.duration_minutes) return formatMinutes(task.duration_minutes);
    if (!task?.started_at || !task?.completed_at) return "-";

    const start = task.started_at.toDate();
    const end = task.completed_at.toDate();

    const diff = Math.floor((end - start) / 60000);
    if (diff < 0) return "-"; // stale/cross-session timestamps — don't show a negative number

    return formatMinutes(diff);
  };

    const statusColor = {
      open: "#888",
      assigned: "#3b82f6",
      in_progress: "#f59e0b",
      completed: "#10b981",
      partial: "#f97316",
      completed_pending_approval: "#8b5cf6",
      closed: "#64748b",
      cancelled: "#ef4444",
      rework: "#f97316"
    };

    return (
      <div style={{
        padding: "30px",
        background: "linear-gradient(180deg, #f8fafc, #eef2f7)",
        minHeight: "100vh"
      }}>

        {/* BACK + LANGUAGE TOGGLE */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "#111",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: 6,
              cursor: "pointer"
            }}
          >
            ← Back
          </button>

          {/* Page-level language switch — technician-only page, so it's safe
              to always show this here (manager's ServiceDetail.js header is
              a separate file and does NOT get this toggle). */}
          <LangToggle lang={lang} setLang={setLang} />
        </div>

        {/* CARD */}
        <div style={{
          background: "#fff",
  padding: "25px",
  borderRadius: "16px",
  boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
  border: "1px solid #f1f1f1",
  maxWidth: "800px"
        }}>

          <h2 style={{ marginBottom: 20 }}>Task Detail</h2>
          <h3 style={{ marginBottom: 5, color: "#111" }}>
    {task?.client_name}
  </h3>

  <p style={{ fontSize: 18, fontWeight: "600", color: "#ef4444", marginBottom: 20 }}>
    {task?.complaint}
  </p>

          {/* GRID */}
          <div style={{
            display: "grid",
  gridTemplateColumns: "1fr 1fr",
  rowGap: "18px",
  columnGap: "30px"
          }}>

            <Info label="Task ID" value={id} />
            <Info label="Complaint" value={task?.complaint} />
            <Info label="Client" value={task?.client_name} />
            <Info label="Site" value={task?.site_name} />
            <Info label="Address" value={task?.address} />
            <Info label="Scheduled Date" value={task?.scheduled_date} />
            <Info label="Work Duration" value={getDuration()} />

            <div>
              <b>Location:</b><br />
              {task?.location_link ? (
                <a href={task.location_link} target="_blank" rel="noreferrer">
                  Open Map
                </a>
              ) : "-"}
            </div>

            <div>
              <b>Status:</b><br />
              <span style={{
                padding: "6px 14px",
  borderRadius: "999px",
  background: statusColor[task?.status],
  color: "#fff",
  fontSize: 12,
  fontWeight: "600",
  letterSpacing: "0.5px"
              }}>
                {STATUS_LABEL_KEYS[task?.status] ? t(STATUS_LABEL_KEYS[task?.status]) : (task?.status || "-")}
              </span>
            </div>

          </div>

          <hr style={{ margin: "25px 0" }} />

          {/* TECH ACTIONS — MRF, Defective, Remark */}
          {task?.status === "in_progress" &&
          (task?.assigned_techs || []).some(t => t.id === techId) && (
            <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 12 }}>

              {/* RAISE MRF */}
              <button
                onClick={() => navigate("/mrf", { state: { type: "service", ref_id: id } })}
                style={{ padding: "10px 16px", background: "#7c3aed", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, cursor: "pointer", textAlign: "left" }}
              >
                📦 Raise MRF
              </button>

              {/* UPLOAD PHOTOS — available any time while job is
                  in_progress, independent of the Complete Job report.
                  Mirrors ServiceDetail.js's (manager-side) same pattern. */}
              <label style={{
                padding: "10px 16px", background: "#2563eb", color: "#fff",
                border: "none", borderRadius: 10, fontWeight: 600,
                cursor: uploadingPhotos ? "not-allowed" : "pointer",
                textAlign: "left", display: "block",
                opacity: uploadingPhotos ? 0.6 : 1,
              }}>
                {uploadingPhotos
                  ? "Uploading..."
                  : `📷 Upload Photos (${(task?.photos || []).length}/${MAX_PHOTOS_PER_JOB})`}
                <input type="file" multiple accept="image/*"
                  onChange={handlePhotoUpload} disabled={uploadingPhotos}
                  style={{ display: "none" }} />
              </label>

              {/* Already-uploaded photos — thumbnails, tech's own view */}
              {task?.photos?.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {task.photos.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer">
                      <img src={url} alt={`photo-${i}`}
                        style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: "1px solid #e2e8f0" }} />
                    </a>
                  ))}
                </div>
              )}

              {/* ✅ ADD MACHINE — client+site pre-filled from job, task_id passed for linking */}
              <button
                onClick={goToAddMachine}
                style={{ padding: "10px 16px", background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, cursor: "pointer", textAlign: "left" }}
              >
                🖥 Add Machine at this Site
              </button>

              {/* REMARK — chat-style thread */}
              <div>
                <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: "#374151" }}>
                  💬 Remarks
                </p>

                <div style={{
                  maxHeight: 280, overflowY: "auto", padding: 12,
                  background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0",
                  marginBottom: 10, display: "flex", flexDirection: "column", gap: 8,
                }}>
                  {(task?.remarks_log || []).length === 0 && (
                    <p style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", margin: 0 }}>
                      No remarks yet
                    </p>
                  )}
                  {(task?.remarks_log || []).map((r, i) => {
                    const isMine = r.by === techId;
                    return (
                      <div key={i} style={{
                        alignSelf: isMine ? "flex-end" : "flex-start",
                        maxWidth: "80%",
                      }}>
                        <div style={{
                          background: isMine ? "#2563eb" : "#e5e7eb",
                          color: isMine ? "#fff" : "#111827",
                          padding: "8px 12px",
                          borderRadius: 12,
                          borderBottomRightRadius: isMine ? 2 : 12,
                          borderBottomLeftRadius: isMine ? 12 : 2,
                          fontSize: 13,
                          wordBreak: "break-word",
                        }}>
                          {r.text}
                        </div>
                        <p style={{
                          fontSize: 10, color: "#94a3b8", margin: "2px 4px 0",
                          textAlign: isMine ? "right" : "left",
                        }}>
                          {r.by_name || "—"} · {r.at ? new Date(r.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleRemark(); }}
                    placeholder="Type a remark..."
                    style={{ ...inputStyle, marginTop: 0, flex: 1 }}
                  />
                  <button onClick={handleRemark}
                    style={{ padding: "10px 18px", background: "#374151", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                    Send
                  </button>
                </div>
              </div>

              {/* DEFECTIVE ITEM */}
              <div>
                <button
                  onClick={() => setShowDefectiveForm(!showDefectiveForm)}
                  style={{ padding: "10px 16px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, cursor: "pointer" }}
                >
                  {showDefectiveForm ? "Cancel" : "🔧 Add Defective Item"}
                </button>

                {showDefectiveForm && (
                  <div ref={defectiveFormRef}>
                  <DefectiveItemForm
                    defectiveItem={defectiveItem}
                    setDefectiveItem={setDefectiveItem}
                    siteMachines={siteMachines}
                    onSave={addDefective}
                    onAddMachine={goToAddMachine}
                    lang={lang}
                    inputStyle={{ ...inputStyle, marginTop: 8 }}
                    containerStyle={{ marginTop: 10, padding: 14, background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 10 }}
                    sectionLabelStyle={{ fontSize: 11, fontWeight: 700, color: "#991b1b", marginTop: 16, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}
                    saveButtonStyle={{ marginTop: 10, width: "100%", padding: "10px 0", background: "#dc2626", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: addingDefective ? "not-allowed" : "pointer", opacity: addingDefective ? 0.6 : 1 }}
                    saveLabel={addingDefective ? "Saving..." : undefined}
                    saveDisabled={addingDefective}
                    emptyStateStyle={{ marginTop: 8, padding: "12px 14px", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 8, fontSize: 13, color: "#92400e" }}
                    addMachineButtonStyle={{ padding: "8px 14px", background: "#0ea5e9", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13 }}
                  />
                  </div>
                )}

                {/* Existing defective items */}
                {task?.defective_items?.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    {task.defective_items.map((item, idx) => {
                      const alreadyRequested = sparePartRequests.some(
                        r => r.linked_defective_item_id === String(idx)
                      );
                      return (
                        <div key={idx} style={{ fontSize: 12, background: "#fff5f5", border: "1px solid #fca5a5", borderRadius: 8, padding: "6px 10px", marginTop: 4 }}>
                          <b>{item.item_name}</b> × {item.qty}
                          {item.machine_model && ` | Machine: ${item.machine_model}`}
                          {item.part_name && ` | Brand: ${item.part_name}`}
                          {item.part_code && ` | Code: ${item.part_code}`}
                          {item.serial_no && ` | Part S/N: ${item.serial_no}`}
                          {alreadyRequested ? (
                            <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: "#16a34a" }}>
                              ✓ Spare Part Already Requested
                            </div>
                          ) : (
                            <button
                              onClick={() => prefillSparePartFromDefective(item, idx)}
                              style={{ display: "block", marginTop: 6, background: "none", border: "none", color: "#7c3aed", fontSize: 11, fontWeight: 700, cursor: "pointer", padding: 0 }}
                            >
                              🔩 Request Replacement Part →
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* SPARE PARTS */}
              <div>
                <button
                  onClick={() => setShowSparePartForm(!showSparePartForm)}
                  style={{ padding: "10px 16px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, cursor: "pointer" }}
                >
                  {showSparePartForm ? "Cancel" : "🔩 Request Spare Part"}
                </button>

                {showSparePartForm && (
                  <div ref={sparePartFormRef} style={{ marginTop: 10, padding: 14, background: "#ecfeff", border: "1px solid #67e8f9", borderRadius: 10 }}>
                    <input
                      placeholder="Part Name *"
                      value={sparePartItem.part_name}
                      onChange={e => setSparePartItem(prev => ({ ...prev, part_name: e.target.value }))}
                      style={inputStyle}
                    />
                    <input
                      placeholder="Part No."
                      value={sparePartItem.part_no}
                      onChange={e => setSparePartItem(prev => ({ ...prev, part_no: e.target.value }))}
                      style={{ ...inputStyle, marginTop: 8 }}
                    />
                    <input
                    placeholder="Serial No. *"
                    value={sparePartItem.serial_no}
                    onChange={e => setSparePartItem(prev => ({ ...prev, serial_no: e.target.value }))}
                    style={{ ...inputStyle, marginTop: 8 }}
                  />
                    <input
                      placeholder="Brand"
                      value={sparePartItem.brand}
                      onChange={e => setSparePartItem(prev => ({ ...prev, brand: e.target.value }))}
                      style={{ ...inputStyle, marginTop: 8 }}
                    />
                    <input
                      type="number"
                      placeholder="Quantity"
                      value={sparePartItem.quantity}
                      onChange={e => setSparePartItem(prev => ({ ...prev, quantity: e.target.value }))}
                      style={{ ...inputStyle, marginTop: 8 }}
                    />
                    <select
                      value={sparePartItem.machine_id}
                      onChange={e => setSparePartItem(prev => ({ ...prev, machine_id: e.target.value }))}
                      style={{ ...inputStyle, marginTop: 8 }}
                    >
                      <option value="">Select Machine (optional)</option>
                      {siteMachines.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.machine_name || "Unnamed"} {m.model_no ? `· ${m.model_no}` : ""}
                        </option>
                      ))}
                    </select>
                  
                    <input
                      placeholder="Notes (optional)"
                      value={sparePartItem.notes}
                      onChange={e => setSparePartItem(prev => ({ ...prev, notes: e.target.value }))}
                      style={{ ...inputStyle, marginTop: 8 }}
                    />
                    <button
                      onClick={submitSparePart}
                      disabled={submittingSparePart}
                      style={{ marginTop: 10, width: "100%", padding: "10px 0", background: "#0891b2", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: submittingSparePart ? "not-allowed" : "pointer", opacity: submittingSparePart ? 0.6 : 1 }}
                    >
                      {submittingSparePart ? "Submitting..." : "✅ Submit Request"}
                    </button>
                  </div>
                )}

                {/* Existing spare part requests for this job */}
                {sparePartRequests.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    {sparePartRequests.map((r) => (
                      <div key={r.id} style={{ fontSize: 12, background: "#ecfeff", border: "1px solid #67e8f9", borderRadius: 8, padding: "6px 10px", marginTop: 4 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <b>{r.part_name}</b>
                          <span style={{ fontSize: 10, fontWeight: 700, color: "#0e7490", background: "#cffafe", padding: "2px 8px", borderRadius: 10 }}>
                            {r.status.replace(/_/g, " ")}
                          </span>
                        </div>
                        {r.part_no && <div>Part No: {r.part_no}</div>}
                        {r.serial_no && <div>S/N: {r.serial_no}</div>}
                        <div style={{ color: "#0891b2" }}>{r.request_no}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ACTION BUTTONS */}
          <div style={{ display: "flex", gap: 10 }}>

            {/* ACCEPT + REJECT — sirf agar tech assigned hai aur accept nahi kiya */}
            {task?.status === "assigned" &&
            (task?.assigned_techs || []).some(t => t.id === techId && t.accepted === null) && (
              <div style={{ display: "flex", gap: 10 }}>
                <ActionBtn text="✋ Accept Job" onClick={handleAccept} color="#3b82f6" />
                <ActionBtn text="✕ Reject" onClick={() => setShowRejectModal(true)} color="#ef4444" />
              </div>
            )}

            {/* START — sirf agar tech ne accept kiya */}
            {task?.status === "assigned" &&
            (task?.assigned_techs || []).some(t => t.id === techId && t.accepted === true) && (
              <ActionBtn
                text={locating ? "📍 Getting location..." : "▶ Start Work"}
                onClick={handleStart}
                color="#f59e0b"
              />
            )}

            {/* COMPLETE */}
            {task?.status === "in_progress" &&
            (task?.assigned_techs || []).some(t => t.id === techId) && (
              <ActionBtn text="✅ Complete Job" onClick={() => setShowReport(true)} color="#10b981" />
            )}

          </div>

        </div>

        {/* REPORT FORM */}
        {showReport && (
    <div ref={reportFormRef} style={{
      marginTop: 30,
      background: "#fff",
      padding: 20,
      borderRadius: 12,
      boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
      maxWidth: "800px"
    }}>
      <h3>Service Report</h3>

      <Input
        name="issue_found"
        placeholder={t("issue_found_placeholder")}
        onChange={handleChange}
      />

      <Input
        name="resolution"
        placeholder={t("work_done_placeholder")}
        onChange={handleChange}
      />

      <textarea
        name="service_report"
        placeholder={t("detailed_report_placeholder")}
        onChange={handleChange}
        style={textareaStyle}
      />

      <select
        name="completion_status"
        onChange={handleChange}
        style={inputStyle}
      >
        <option value="completed">Completed</option>
        <option value="partial">Partial</option>
        <option value="not_resolved">Not Resolved</option>
      </select>

      {/* PAYMENT SECTION — mandatory */}
      <div style={{
        marginTop: 16, padding: 14,
        background: payment.received === null ? "#fff7ed" : payment.received ? "#f0fdf4" : "#f8fafc",
        border: `2px solid ${payment.received === null ? "#f59e0b" : payment.received ? "#16a34a" : "#e2e8f0"}`,
        borderRadius: 10,
      }}>
        <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
          💰 Payment Collected? <span style={{ color: "#ef4444" }}>*</span>
        </p>
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <button
            onClick={() => setPayment(p => ({ ...p, received: true }))}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8, border: "2px solid",
              borderColor: payment.received === true ? "#16a34a" : "#e2e8f0",
              background: payment.received === true ? "#16a34a" : "#fff",
              color: payment.received === true ? "#fff" : "#374151",
              fontWeight: 700, cursor: "pointer", fontSize: 14,
            }}
          >
            ✅ Yes, Received
          </button>
          <button
            onClick={() => setPayment(p => ({ ...p, received: false, amount: "" }))}
            style={{
              flex: 1, padding: "10px 0", borderRadius: 8, border: "2px solid",
              borderColor: payment.received === false ? "#ef4444" : "#e2e8f0",
              background: payment.received === false ? "#ef4444" : "#fff",
              color: payment.received === false ? "#fff" : "#374151",
              fontWeight: 700, cursor: "pointer", fontSize: 14,
            }}
          >
            ❌ No
          </button>
        </div>

        {payment.received === null && (
          <p style={{ fontSize: 12, color: "#f59e0b", fontWeight: 600 }}>
            ⚠️ Please select payment status
          </p>
        )}

        {payment.received === true && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input
              type="number"
              placeholder="Amount ₹ *"
              value={payment.amount}
              onChange={e => setPayment(p => ({ ...p, amount: e.target.value }))}
              style={{ ...inputStyle, marginTop: 0, flex: 1 }}
            />
            <select
              value={payment.mode}
              onChange={e => setPayment(p => ({ ...p, mode: e.target.value }))}
              style={{ ...inputStyle, marginTop: 0, flex: 1 }}
            >
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
        )}
      </div>

      <button
        onClick={handleComplete}
        disabled={submitting || payment.received === null}
        style={{
          ...submitBtn,
          opacity: (submitting || payment.received === null) ? 0.5 : 1,
          cursor: (submitting || payment.received === null) ? "not-allowed" : "pointer",
        }}
      >
        {submitting ? "Submitting..." : "Submit Report"}
      </button>
    </div>
  )}
  {task?.status === "completed" && (
    <div style={{ marginTop: 20 }}>
      <h3>Final Report</h3>

      <p><b>Issue:</b> {task.issue_found}</p>
      <p><b>Work Done:</b> {task.resolution}</p>
      <p><b>Report:</b> {task.service_report}</p>
    </div>
  )}

      {/* REJECT MODAL */}
        {showRejectModal && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.6)", zIndex: 999,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16
          }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 24, width: "100%", maxWidth: 400 }}>
              <h3 style={{ marginBottom: 8 }}>Reject Job</h3>
              <p style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
                Reason mandatory — SM ko notification jayegi.
              </p>
              <textarea
                placeholder="Enter rejection reason..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={3}
                style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #ddd", resize: "none", fontSize: 14 }}
                autoFocus
              />
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button
                  onClick={() => { setShowRejectModal(false); setRejectReason(""); }}
                  style={{ flex: 1, padding: 12, borderRadius: 8, border: "1px solid #ddd", cursor: "pointer", background: "#fff" }}>
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  style={{ flex: 1, padding: 12, borderRadius: 8, background: "#ef4444", color: "#fff", border: "none", fontWeight: 700, cursor: "pointer" }}>
                  Confirm Reject
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }

  /* 🔹 SMALL COMPONENTS */

  const Info = ({ label, value }) => (
    <div>
      <b>{label}:</b><br />
      {value || "-"}
    </div>
  );

  const ActionBtn = ({ text, onClick, color }) => (
    <button
      onClick={onClick}
      style={{
        background: `linear-gradient(135deg, ${color}, ${color}99)`,
  color: "#fff",
  border: "none",
  padding: "10px 16px",
  borderRadius: 10,
  cursor: "pointer",
  fontWeight: "600",
  boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
  transition: "0.2s"
      }}
    >
      {text}
    </button>
  );

  const inputStyle = {
    width: "100%",
    padding: "10px",
    marginTop: "10px",
    borderRadius: "6px",
    border: "1px solid #ddd"
  };

  const textareaStyle = {
    ...inputStyle,
    height: "80px"
  };

  const submitBtn = {
    marginTop: "15px",
    background: "#111",
  color: "#fff",
  border: "none",
  padding: "10px 18px",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: "500"
  };

  const Input = ({ name, placeholder, onChange }) => (
    <input
      name={name}
      placeholder={placeholder}
      onChange={onChange}
      style={inputStyle}
    />
  );
